import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import type {
  CatalogUnit,
  CatalogUnitsQuery,
  CatalogValidation,
  ConfirmCatalogInput,
  ConfirmCatalogResult,
  Project,
  Paginated,
} from '../../../types/admin';
import { useClaveInmobiliaria } from '../inmobiliaria-activa/inmobiliaria-activa';

export function useCatalogProjects() {
  const inmobiliaria = useClaveInmobiliaria();
  return useQuery<Paginated<Project>>({
    queryKey: ['projects', inmobiliaria, 1, 100],
    queryFn: () => apiClient.projects.list({ page: 1, pageSize: 100 }),
  });
}

export function useValidateCatalog() {
  return useMutation<CatalogValidation, Error, { proyectoId: string; archivo: File }>({
    mutationFn: ({ proyectoId, archivo }) => apiClient.units.catalog.validate(proyectoId, archivo),
  });
}

export function useConfirmCatalog() {
  const queryClient = useQueryClient();
  return useMutation<ConfirmCatalogResult, Error, ConfirmCatalogInput>({
    mutationFn: (input) => apiClient.units.catalog.confirm(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * change-70: página del catálogo (`GET /units`). La clave incluye la inmobiliaria activa (fix-60) y
 * todos los filtros; `keepPreviousData` evita el parpadeo al paginar o filtrar.
 */
export function useCatalogUnits(query: CatalogUnitsQuery | null) {
  const inmobiliaria = useClaveInmobiliaria();
  return useQuery<Paginated<CatalogUnit>>({
    queryKey: ['units', inmobiliaria, query],
    queryFn: () => apiClient.units.list(query as CatalogUnitsQuery),
    enabled: query !== null,
    placeholderData: keepPreviousData,
  });
}

/** HU-A06: ajuste del precio de lista de una unidad (`catalogo.editar`). */
export function useAdjustPrice() {
  const queryClient = useQueryClient();
  return useMutation<CatalogUnit, Error, { id: string; precioLista: number }>({
    mutationFn: ({ id, precioLista }) => apiClient.units.adjustPrice(id, precioLista),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });
}
